// Humanize the structured `[TAG] ...` + `private(...)='X' vs public(...)='Y'`
// notation that the backend writes into `TriggeredFlag.fact_summary`, and
// linkify bare URLs in both `fact_summary` and `source_citation`.
//
// This is a pure-string -> HTML-string transform; the result is safe to inject
// via `dangerouslySetInnerHTML` because every input is HTML-escaped BEFORE any
// link/tag markup is inserted. Flag text is backend/AI-generated (not user
// input) but we keep it safe regardless.

// ---- Source-label map -------------------------------------------------------
// Lowercased raw source names (as they appear inside `public(name (...))`)
// mapped to a friendly business label.
export const SOURCE_LABELS: Record<string, string> = {
  edgar: 'EDGAR',
  sec: 'SEC',
  finra: 'FINRA',
  otc_markets: 'OTC Markets',
  otcmarkets: 'OTC Markets',
  yahoo: 'Yahoo',
  yahoo_finance: 'Yahoo Finance',
  ofac: 'OFAC',
  searxng: 'web search',
  sedar: 'SEDAR',
  sedi: 'SEDI',
  bloomberg: 'Bloomberg',
  reuters: 'Reuters',
  google: 'Google',
};

// Known `[TAG]` prefixes from the backend -> friendly badge label.
const TAG_LABELS: Record<string, string> = {
  'PRIVATE-PUBLIC MATCH': 'Private/Public Match',
  'CONSISTENCY': 'Consistency',
  'NOT VERIFIABLE': 'Not Verifiable',
  'MISSING': 'Missing',
  'MISMATCH': 'Mismatch',
  'FORMAT': 'Format',
  'THRESHOLD': 'Threshold',
  'CROSS-CHECK': 'Cross-check',
};

// ---- Helpers ----------------------------------------------------------------

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Title-case a raw identifier: `otc_markets` -> `Otc Markets`.
function titleCase(raw: string): string {
  return raw
    .replace(/[_\-]+/g, ' ')
    .replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
}

function sourceLabel(rawName: string): string {
  const key = rawName.trim().toLowerCase();
  if (SOURCE_LABELS[key]) return SOURCE_LABELS[key];
  return titleCase(rawName);
}

// Convert a Python-list repr like `['cover_page', 'legal_opinion']` into a
// human list "cover page, legal opinion". Accepts single quoted strings and
// returns the original string if it doesn't look like a list.
function humanizePythonListRepr(s: string): string {
  const trimmed = s.trim();
  // Match `['a', 'b', ...]` (single or double quoted).
  const m = trimmed.match(/^\[\s*(.+?)\s*\]$/s);
  if (!m) {
    // Not a list repr: still humanize a single bare value by stripping
    // surrounding quotes and replacing underscores with spaces.
    return trimmed.replace(/^['"]|['"]$/g, '').replace(/_/g, ' ');
  }
  const inner = m[1];
  const items = inner
    .split(',')
    .map((it) => it.trim().replace(/^['"]|['"]$/g, '').trim())
    .filter((it) => it.length > 0);
  if (items.length === 0) return s;
  if (items.length === 1) return items[0].replace(/_/g, ' ');
  const last = items[items.length - 1].replace(/_/g, ' ');
  const rest = items.slice(0, -1).map((it) => it.replace(/_/g, ' '));
  return `${rest.join(', ')} and ${last}`;
}

// Linkify bare URLs and [label](url) markdown links in an ALREADY-ESCAPED
// HTML string. The input is assumed to have had `escapeHtml` applied, so
// URLs here are the escaped form (which does not change URL characters
// except `&` becomes `&amp;` — handled by matching the escaped text).
//
// Order: markdown links first (so the URL inside parens is consumed), then
// bare URLs. We use the escaped `&amp;` form in the regex for query strings.
export function linkifyUrls(escapedHtml: string): string {
  let out = escapedHtml;
  // Markdown links: [label](url). URL may contain `&amp;` (escaped `&`).
  out = out.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+?)(?=[)\s]|$)\)/g,
    (_m, label: string, url: string) =>
      `<a href="${url}" class="text-primary-600 underline" target="_blank" rel="noopener noreferrer">${label}</a>`,
  );
  // Bare URLs. Skip any URL already inside an `href="..."` attribute (so
  // we don't double-wrap the URL emitted by the markdown-link pass above):
  // negative lookbehind for `href="` + exclude `"` from the URL body so a
  // trailing closing quote is never consumed. Boundary is whitespace, `)`,
  // or end-of-string (same as before).
  out = out.replace(
    /(?<!href=")(https?:\/\/[^\s<"]+)(?=[)\s]|$)/g,
    (match: string) =>
      `<a href="${match}" class="text-primary-600 underline" target="_blank" rel="noopener noreferrer">${match}</a>`,
  );
  return out;
}

// Humanize the `private(...)='X' vs public(...)='Y'` sub-pattern.
// Returns the humanized sentence (already HTML-escaped, NOT yet linkified —
// linkifyUrls is applied after so the URL inside the humanized text becomes a
// link). Returns null if the pattern doesn't match.
//
// Example input (raw, un-escaped):
//   `[PRIVATE-PUBLIC MATCH] cik mismatch: private(['cover_page', 'legal_opinion'])='0001716621' vs public(edgar (https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=1716621&type=&dateb=&owner=include&count=40))='1716621'`
function humanizePrivatePublicMismatch(text: string): string | null {
  // Match: private(LHS)='VAL1' vs public(RHS)='VAL2'
  // LHS / RHS can contain nested parens (e.g. `edgar (https://...)`) so we
  // use a balanced-paren scan rather than a naive regex.
  const idxPrivate = text.indexOf('private(');
  if (idxPrivate < 0) return null;
  const idxVs = text.indexOf(' vs public(', idxPrivate);
  if (idxVs < 0) return null;

  // Anything before `private(` is the prefix (e.g. "cik mismatch: ").
  const prefix = text.slice(0, idxPrivate).trim();

  // Extract the content inside `private(...)` with balanced parens.
  // `privOpenParen` is the index of the `(` that opens `private(`.
  const privOpenParen = idxPrivate + 'private'.length;
  const privClose = findBalancedClose(text, privOpenParen);
  if (privClose < 0) return null;
  // Inner content is between the open paren and its matching close.
  const privInner = text.slice(privOpenParen + 1, privClose);

  // Then `='VAL1'` after the closing paren.
  const val1Match = text.slice(privClose + 1).match(/^\s*=\s*'((?:[^'\\]|\\.)*)'/);
  if (!val1Match) return null;
  const val1 = val1Match[1];

  // Now `vs public(...)='VAL2'`.
  const pubOpenParen = idxVs + ' vs public'.length;
  const pubClose = findBalancedClose(text, pubOpenParen);
  if (pubClose < 0) return null;
  const pubInner = text.slice(pubOpenParen + 1, pubClose);

  const val2Match = text.slice(pubClose + 1).match(/^\s*=\s*'((?:[^'\\]|\\.)*)'/);
  if (!val2Match) return null;
  const val2 = val2Match[1];

  // Build prose. privInner is a python-list repr of source docs. pubInner is
  // `source_name (url)` or just `source_name`.
  const privateDocs = humanizePythonListRepr(privInner);
  const { name: pubName, url: pubUrl } = splitSourceNameAndUrl(pubInner);
  const pubLabel = sourceLabel(pubName);

  const privPart = `private documents (${privateDocs}) show \`${val1}\``;
  // Render the public source as a markdown link whose VISIBLE text is the
  // friendly label (e.g. "EDGAR") and whose URL is hidden in the href -- so
  // business users see a clean clickable label, not a long SEC/EDGAR URL.
  const pubPart = pubUrl
    ? `[${pubLabel}](${pubUrl}) shows \`${val2}\``
    : `${pubLabel} shows \`${val2}\``;

  const friendlyPrefix = prefix.replace(/_/g, ' ');
  const sentence = friendlyPrefix
    ? `${capitalizeFirst(friendlyPrefix)} ${privPart}; ${pubPart}.`
    : `${capitalizeFirst(privPart)} vs ${pubPart}.`;

  // Escape + wrap inline code in <code>. We escape the whole string, then
  // re-wrap the backticked value spans as <code> elements.
  let escaped = escapeHtml(sentence);
  // Convert `escaped-val` backtick spans into <code>. After escaping, the
  // backticks are still literal backticks.
  escaped = escaped.replace(/`([^`]+)`/g, '<code class="text-xs bg-slate-100 px-1 rounded">$1</code>');
  return escaped;
}

// Split `edgar (https://...)` -> { name: 'edgar', url: 'https://...' }.
// If no URL, returns { name: whole, url: '' }.
function splitSourceNameAndUrl(inner: string): { name: string; url: string } {
  const trimmed = inner.trim();
  const m = trimmed.match(/^([^\s(]+)\s*\((https?:\/\/[^\s)]+)\)\s*$/);
  if (m) return { name: m[1], url: m[2] };
  // Maybe `name (label)` without URL — just take the name.
  const m2 = trimmed.match(/^([^\s(]+)\s*\(([^)]*)\)\s*$/);
  if (m2) return { name: m2[1], url: '' };
  return { name: trimmed, url: '' };
}

// Capitalize the first alphabetic character of a string.
function capitalizeFirst(s: string): string {
  return s.replace(/^([a-z])/, (m) => m.toUpperCase());
}

// Given text and the index of an opening `(`, return the index of the
// matching `)`. Returns -1 if unbalanced.
function findBalancedClose(text: string, openParenIdx: number): number {
  let depth = 0;
  for (let i = openParenIdx; i < text.length; i++) {
    const ch = text[i];
    if (ch === '(') depth++;
    else if (ch === ')') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

// ---- Main entry point -------------------------------------------------------

export interface HumanizedFlag {
  // Friendly label for the `[TAG]` prefix, or undefined if there was no tag.
  badgeLabel?: string;
  // The raw `[TAG]` token (e.g. `[PRIVATE-PUBLIC MATCH]`) if present, so the
  // caller can know which tag was stripped.
  badgeTag?: string;
  // HTML string safe for dangerouslySetInnerHTML. URLs linkified, mismatches
  // humanized, code spans wrapped.
  bodyHtml: string;
}

// Humanize a fact_summary string into a badge label + HTML body.
export function humanizeFactSummary(text: string): HumanizedFlag {
  const raw = text ?? '';
  let working = raw.trim();
  let badgeLabel: string | undefined;
  let badgeTag: string | undefined;

  // Strip a leading `[TAG]` (tag may contain spaces, e.g. `[NOT VERIFIABLE]`).
  const tagMatch = working.match(/^\[([A-Z][A-Z0-9 _\-]*)\]\s*/);
  if (tagMatch) {
    const tag = tagMatch[1];
    badgeTag = `[${tag}]`;
    badgeLabel = TAG_LABELS[tag] || titleCase(tag);
    working = working.slice(tagMatch[0].length);
  }

  // Try the private/public mismatch humanizer first.
  const humanized = humanizePrivatePublicMismatch(working);
  let html: string;
  if (humanized != null) {
    html = humanized;
  } else {
    html = escapeHtml(capitalizeFirst(working));
  }

  // Linkify URLs in the resulting HTML. For the humanized mismatch path the
  // URL is already escaped and sits inside `(...)` — linkifyUrls will wrap it.
  // For the fallback path the whole text is escaped, then linkified.
  html = linkifyUrls(html);

  return { badgeLabel, badgeTag, bodyHtml: html };
}

// Humanize a source_citation string into an HTML string. The backend writes
// citations in two shapes:
//   `edgar (https://www.sec.gov/cgi-bin/browse-edgar?...)`  -- name + URL
//   `https://...`                                          -- bare URL
// Both are rendered as a clickable hyperlink whose visible text is a friendly
// label (e.g. "EDGAR", "Source") so business users never see the raw URL.
export function humanizeSourceCitation(text: string): string {
  const raw = (text ?? '').trim();
  if (!raw) return '';

  // `name (url)` -> labeled link [Source Label](url).
  const m = raw.match(/^([^()]+?)\s*\((https?:\/\/[^\s)]+)\)\s*$/);
  if (m) {
    const label = sourceLabel(m[1].trim());
    return linkifyUrls(escapeHtml(`[${label}](${m[2]})`));
  }

  // Bare URL -> [Source](url).
  if (/^https?:\/\/\S+$/i.test(raw)) {
    return linkifyUrls(escapeHtml(`[Source](${raw})`));
  }

  // Fallback: escape + linkify any bare URLs that appear inline in free text.
  return linkifyUrls(escapeHtml(raw));
}